import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFolder, faChevronDown, faChevronUp, faCheck, faPlus, faSpinner, faSearch, faFilePdf, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { getFolderDisplayLabel } from '../utils/folderHierarchy';
import { getUserData } from '../utils/userUtils';
import { useAccount } from '../contexts/AccountContext';
import './CreateCampaignModal.css';

/**
 * CreateCampaignModal - Modal for creating campaigns from media plan channels
 * 
 * Props:
 * - isOpen: boolean - Whether the modal is open
 * - onClose: function - Called when modal should close
 * - channel: object - The channel data from media plan
 * - mediaPlanName: string - Name of the media plan for campaign naming
 * - insertionOrderFile: object - Pre-uploaded file data { file, fileData }
 * - onSuccess: function - Called after successful campaign creation
 */
const CreateCampaignModal = ({ 
  isOpen, 
  onClose, 
  channel, 
  mediaPlanName, 
  insertionOrderFile,
  onSuccess 
}) => {
  const navigate = useNavigate();
  const { getAccountIdToUse, accounts } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();
  // MMGY accounts default the YouTube DV360 inventory mode to 'Standard';
  // everyone else defaults to 'Expanded'. Mirrors the backend handler's
  // per-account fallback in youtube_dv360_api_handler.
  const accountName = accounts?.find(a => String(a.id) === String(accountId))?.name || '';
  const isMMGYAccount = accountName && (accountName.toLowerCase() === 'mmgy' || accountName.toLowerCase().startsWith('mmgy-'));

  // Form state
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [showNewFolderForm, setShowNewFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [folderSearchTerm, setFolderSearchTerm] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [adAccountId, setAdAccountId] = useState('');
  // YouTube DV360 launcher accepts an --inventory_mode CLI arg with two
  // values: 'Standard' or 'Expanded'. The campaign-services handler
  // falls back to 'Standard' for MMGY and 'Expanded' otherwise when no
  // value is provided; we let the operator override here.
  const [inventoryMode, setInventoryMode] = useState('Expanded');

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const folderDropdownRef = useRef(null);
  const searchDebounceRef = useRef(null);

  // Map platform name to destination ID
  const mapPlatformToDestination = (platform) => {
    const platformLower = platform?.toLowerCase() || '';
    if (platformLower.includes('facebook') || platformLower.includes('meta')) return 'facebook';
    if (platformLower.includes('google') && platformLower.includes('search')) return 'google-ads-search';
    if (platformLower.includes('google') && platformLower.includes('display')) return 'dv360';
    if (platformLower.includes('dv360')) return 'dv360';
    if (platformLower.includes('youtube')) return 'youtube-dv360';
    if (platformLower.includes('trade desk') || platformLower.includes('ttd')) return 'ttd';
    if (platformLower.includes('linkedin')) return 'linkedin';
    if (platformLower.includes('reddit')) return 'reddit';
    if (platformLower.includes('madhive')) return 'madhive';
    if (platformLower.includes('groundtruth')) return 'groundtruth';
    return platformLower.replace(/\s+/g, '-');
  };

  const destination = channel ? mapPlatformToDestination(channel.platform) : '';
  const requiresAdAccountId = ['facebook', 'linkedin'].includes(destination);

  // Initialize campaign name from props
  useEffect(() => {
    if (isOpen && channel && mediaPlanName) {
      const baseName = mediaPlanName || 'Campaign';
      setCampaignName(`${baseName} - ${channel.platform}`);
    }
  }, [isOpen, channel, mediaPlanName]);

  // Reset inventory mode default when the modal opens or MMGY status
  // becomes known (accounts loads asynchronously from AccountContext).
  useEffect(() => {
    if (isOpen) {
      setInventoryMode(isMMGYAccount ? 'Standard' : 'Expanded');
    }
  }, [isOpen, isMMGYAccount]);

  // Fetch folders on open
  useEffect(() => {
    if (isOpen && accountId) {
      fetchFolders();
    }
  }, [isOpen, accountId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFolder(null);
      setShowFolderDropdown(false);
      setShowNewFolderForm(false);
      setNewFolderName('');
      setFolderSearchTerm('');
      setAdAccountId('');
      setError(null);
    }
  }, [isOpen]);

  const fetchFolders = useCallback(async (searchTerm = '') => {
    if (!accountId) return;
    try {
      setIsLoading(true);
      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: {
          accountId,
          userId: user?.userId || user?.id,
          search: searchTerm,
          page: 1,
          limit: 50,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const newFolders = response.data.folders || response.data || [];
      setFolders(newFolders);
    } catch (err) {
      console.error('Error fetching folders:', err);
      setError('Failed to load folders');
    } finally {
      setIsLoading(false);
    }
  }, [accountId, user]);

  const handleFolderSearch = useCallback((searchTerm) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      fetchFolders(searchTerm);
    }, 300);
  }, [fetchFolders]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !accountId) return;
    try {
      setIsLoading(true);
      const response = await api.post(getApiUrl('/api/v2/folders'), {
        name: newFolderName.trim(),
        accountId,
        userId: user?.userId || user?.id
      });
      const newFolder = response.data;
      setFolders(prev => [...prev, newFolder]);
      setSelectedFolder(newFolder);
      setNewFolderName('');
      setShowNewFolderForm(false);
      setShowFolderDropdown(false);
    } catch (err) {
      console.error('Error creating folder:', err);
      setError('Failed to create folder');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selectedFolder) {
      setError('Please select a folder');
      return;
    }
    if (!campaignName.trim()) {
      setError('Please enter a campaign name');
      return;
    }
    if (requiresAdAccountId && !adAccountId.trim()) {
      setError('Ad Account ID is required for this platform');
      return;
    }

    try {
      setIsSubmitting(true);

      // Prepare campaign data
      const campaignData = {
        name: campaignName.trim(),
        folderId: selectedFolder.id,
        destination: destination,
        accountId,
        userId: user?.userId || user?.id
      };

      // Add ad account ID if required
      if (requiresAdAccountId) {
        campaignData.adAccountId = adAccountId.trim();
      }

      // YouTube DV360 only — operator-selected inventory mode flows
      // through orchestration metadata to the youtube_dv360_api_handler,
      // which passes it as --inventory_mode to the launcher CLI.
      if (destination === 'youtube-dv360' && inventoryMode) {
        campaignData.inventoryMode = inventoryMode;
      }

      // Add file data if available
      if (insertionOrderFile?.fileData) {
        campaignData.files = [{
          originalName: insertionOrderFile.fileData.originalName,
          gcsPath: insertionOrderFile.fileData.gcsPath,
          publicUrl: insertionOrderFile.fileData.publicUrl
        }];

        // Enable orchestration when files are present (same as CampaignCreation)
        // Use fast-track-shell since we only have the insertion order PDF
        campaignData.orchestration = {
          enabled: true,
          type: 'fast-track-shell',
          priority: 'normal',
          autoStart: true,
          message: `Auto-triggered orchestration for campaign: ${campaignName.trim()}`
        };

        console.log(`📤 Creating campaign with orchestration: fast-track-shell`);
      }

      // Create campaign
      const response = await api.post(getApiUrl('/api/v2/campaigns'), campaignData, {
        params: { accountId, userId: user?.userId || user?.id }
      });

      console.log('Campaign created:', response.data);
      const campaign = response.data;

      if (onSuccess) {
        onSuccess(campaign);
      }

      onClose();

      // Navigate to the campaign detail page with orchestration state
      if (campaign?.id) {
        const successMessage = campaign.orchestration?.triggered
          ? `Campaign "${campaign.name}" created and orchestration started!`
          : `Campaign "${campaign.name}" created successfully!`;

        navigate(`/campaigns/${campaign.id}`, {
          state: {
            fromCampaignCreation: true,
            orchestrationTriggered: campaign.orchestration?.triggered || false,
            orchestrationChatId: campaign.orchestration?.chatId,
            orchestrationType: campaign.orchestration?.type,
            orchestrationStatus: campaign.orchestration?.status,
            orchestrationError: campaign.orchestration?.error,
            message: successMessage
          }
        });
      }
    } catch (err) {
      console.error('Error creating campaign:', err);
      setError(err.response?.data?.error || 'Failed to create campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-campaign-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Campaign</h2>
          <button className="close-button" onClick={onClose} disabled={isSubmitting}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="modal-error">
                {error}
                <button type="button" onClick={() => setError(null)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}

            {/* Platform Display */}
            <div className="form-group">
              <label>Platform</label>
              <div className="platform-display">
                <span className="platform-badge">{channel?.platform}</span>
                <span className="platform-budget">${channel?.budget?.toLocaleString()}</span>
              </div>
            </div>

            {/* Campaign Name */}
            <div className="form-group">
              <label htmlFor="modal-campaign-name">Campaign Name *</label>
              <input
                type="text"
                id="modal-campaign-name"
                className="form-control"
                placeholder="Enter campaign name"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>

            {/* Folder Selection */}
            <div className="form-group">
              <label>Select Folder *</label>
              <div className="folder-select-container">
                <button
                  type="button"
                  className={`folder-select-button ${showFolderDropdown ? 'active' : ''}`}
                  onClick={() => setShowFolderDropdown(!showFolderDropdown)}
                  disabled={isSubmitting}
                >
                  <FontAwesomeIcon icon={faFolder} />
                  <span>{selectedFolder ? getFolderDisplayLabel(selectedFolder) : 'Choose folder...'}</span>
                  <FontAwesomeIcon icon={showFolderDropdown ? faChevronUp : faChevronDown} className="dropdown-arrow" />
                </button>

                {showFolderDropdown && (
                  <div className="folder-dropdown">
                    <div className="folder-search-container">
                      <FontAwesomeIcon icon={faSearch} className="folder-search-icon" />
                      <input
                        type="text"
                        className="folder-search-input"
                        placeholder="Search folders..."
                        value={folderSearchTerm}
                        onChange={(e) => {
                          setFolderSearchTerm(e.target.value);
                          handleFolderSearch(e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>

                    <div className="folder-list-container" ref={folderDropdownRef}>
                      {folders.length === 0 && !isLoading ? (
                        <div className="folder-empty-state">
                          <p>No folders found</p>
                        </div>
                      ) : (
                        folders.map(folder => (
                          <button
                            key={folder.id}
                            type="button"
                            className={`folder-option ${selectedFolder?.id === folder.id ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedFolder(folder);
                              setShowFolderDropdown(false);
                              setFolderSearchTerm('');
                            }}
                          >
                            <FontAwesomeIcon icon={faFolder} />
                            <span>{getFolderDisplayLabel(folder)}</span>
                            {selectedFolder?.id === folder.id && (
                              <FontAwesomeIcon icon={faCheck} className="check-icon" />
                            )}
                          </button>
                        ))
                      )}
                    </div>

                    <div className="folder-dropdown-divider" />

                    {!showNewFolderForm ? (
                      <button
                        type="button"
                        className="folder-option create-new"
                        onClick={() => setShowNewFolderForm(true)}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>Create new folder</span>
                      </button>
                    ) : (
                      <div className="new-folder-form">
                        <input
                          type="text"
                          placeholder="Enter folder name"
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleCreateFolder();
                            }
                          }}
                          autoFocus
                        />
                        <div className="new-folder-actions">
                          <button type="button" className="btn btn-sm btn-primary" onClick={handleCreateFolder} disabled={!newFolderName.trim() || isLoading}>
                            Create
                          </button>
                          <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setShowNewFolderForm(false); setNewFolderName(''); }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Ad Account ID for Facebook/LinkedIn */}
            {requiresAdAccountId && (
              <div className="form-group">
                <label htmlFor="modal-ad-account-id">Ad Account ID *</label>
                <input
                  type="text"
                  id="modal-ad-account-id"
                  className="form-control"
                  placeholder={destination === 'facebook' ? 'Enter Facebook Ad Account ID' : 'Enter Ad Account ID'}
                  value={adAccountId}
                  onChange={(e) => setAdAccountId(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
                <p className="form-help-text">
                  {destination === 'facebook' ? 'Your Facebook Ad Account ID (e.g., 123456789)' : 'Your Ad Account ID'}
                </p>
              </div>
            )}

            {/* YouTube DV360: Inventory Mode (controls launcher --inventory_mode CLI arg). */}
            {destination === 'youtube-dv360' && (
              <div className="form-group">
                <label htmlFor="modal-inventory-mode">Inventory Mode</label>
                <select
                  id="modal-inventory-mode"
                  className="form-control"
                  value={inventoryMode}
                  onChange={(e) => setInventoryMode(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="Standard">Standard</option>
                  <option value="Expanded">Expanded</option>
                </select>
                <p className="form-help-text">
                  Default: <strong>{isMMGYAccount ? 'Standard' : 'Expanded'}</strong>
                  {isMMGYAccount ? ' (MMGY accounts use Standard inventory)' : ''}
                </p>
              </div>
            )}

            {/* Insertion Order File */}
            {insertionOrderFile?.fileData && (
              <div className="form-group">
                <label>Insertion Order (PDF)</label>
                <a
                  href={insertionOrderFile.fileData.publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-preview"
                >
                  <FontAwesomeIcon icon={faFilePdf} />
                  <span className="file-name">{insertionOrderFile.fileData.originalName}</span>
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="external-link-icon" />
                </a>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || !selectedFolder || !campaignName.trim() || (requiresAdAccountId && !adAccountId.trim())}
            >
              {isSubmitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  {' '}Creating...
                </>
              ) : (
                'Create Campaign'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCampaignModal;

